import { gql } from 'graphql-request'
import type { Ref } from 'vue'
import * as R from 'remeda'
import type { GwPaginationEmitProps } from '../../../../components/lib/GwPagination/gwPaginationUtils'
import type {
  ColumnFieldName,
  ReportTableRow,
  HandFieldName,
  HandsStatsFieldName,
  OnlyColumnFieldName,
  TFilterField,
  FilterType,
  ApiFilter,
  ApiFilterType,
  AdditionalHandField,
} from '../../../../types/api/reports'
import { graphQLClient } from '../../gqlClient'
import type { HandStatistics, SimpleTableDataResponses } from '../../../../types/api/hands'

export const handId = 'hand_id'

export const handsQueryKey = ['hands']
export const handsStatisticsQueryKey = ['hands', 'statistics']
export const simpleTableQueryKey = ['hands', 'simpleTable']
export const scoreBreakdownQueryKey = ['hands', 'scoreBreakdown']

const columnsToFieldsParams = {
  actions_with_correctness: [
    `actions_with_correctness {
     action_code
     correctness
    }`,
  ],
} as const

const isField = (item: ColumnFieldName | HandFieldName): item is HandFieldName =>
  columnsToFieldsParams[item as OnlyColumnFieldName] === undefined

const columnsToFields = (columns: ColumnFieldName[]): HandFieldName[] =>
  columns.map(column => (isField(column) ? column : columnsToFieldsParams[column])).flat()

const filterKey = (filter_type: FilterType, key: string): string => {
  const suffix =
    filter_type === 'MULTIPLE_CHOICE'
      ? '__in'
      : filter_type === 'RANGE'
        ? '__range'
        : filter_type === 'SEARCH_TEXT'
          ? '__icontains'
          : ''

  return `${key}${suffix}`
}

type SingleChoiceFilterValue = string | boolean
type MultivalueFilterValue = string[]
type FilterValue = SingleChoiceFilterValue | MultivalueFilterValue

const filterToString = (apiFilterType: ApiFilterType) => (value: SingleChoiceFilterValue) =>
  apiFilterType === 'STRING' ? `"${value}"` : Array.isArray(value) ? `[${value.join(',')}]` : value

const filterValue = (apiFilter: ApiFilter, value: FilterValue) => {
  const valueToString = filterToString(apiFilter.type)

  // TODO add typeguards for single and multivalue filters
  return ['SINGLE_CHOICE', 'SEARCH_TEXT'].includes(apiFilter.filter_type)
    ? valueToString(value as SingleChoiceFilterValue)
    : `[${(value as MultivalueFilterValue).map(valueToString).join(',')}]`
}

type FilterParam = { filter: TFilterField; value: FilterValue }
export type FilterParams = FilterParam[]

const filterQuery = (filterParams: FilterParams) =>
  R.pipe(
    filterParams,
    R.flatMap(filterParam =>
      filterParam.filter.api_filters.map(apiFilter => {
        if (apiFilter.predefined_value === null) {
          return `${filterKey(apiFilter.filter_type, apiFilter.field_name)}: ${filterValue(apiFilter, filterParam.value)}`
        }
        return `${apiFilter.field_name}: ${filterValue(apiFilter, apiFilter.predefined_value as SingleChoiceFilterValue)}`
      }),
    ),
    R.unique(),
    R.join(','),
  )

export const filteredBy = (filters?: FilterParams) =>
  `${filters ? `filtered_by: { ${filterQuery(filters)} }` : ''}`

export type SummaryColumnsT = Partial<Record<ColumnFieldName, HandsStatsFieldName>>

const additionalHandFields: AdditionalHandField[] = ['note', 'color_hex']

const handsQuery = (
  fields: HandFieldName[],
  pagination: GwPaginationEmitProps,
  summaryColumns: SummaryColumnsT,
  ordering?: string,
  filters?: FilterParams,
) => gql`
  query Hands {
    hands (
      limit: ${pagination.limit},
      offset: ${pagination.offset}
      ${ordering ? `, ordered_by: "${ordering}"` : ''}
      ${filteredBy(filters)}
    ) {
      ${fields.join('\n')}
    }
    hand_statistics (
      ${filteredBy(filters)}
    ) {
      count_hands, ${Object.values(summaryColumns).join('\n')}
    }
  }
`

export type OrderParams<T extends string> = T | `-${T}`

type GetHandsParams = {
  fields: Ref<ColumnFieldName[]>
  pagination: Ref<GwPaginationEmitProps>
  ordering: Ref<OrderParams<ColumnFieldName> | undefined>
  filters: Ref<FilterParams>
  summaryColumns: Ref<SummaryColumnsT>
}

export type GetHandsResponse = {
  hands: ReportTableRow[]
  hand_statistics: Partial<Record<HandsStatsFieldName, number>>
}

export const getHands =
  ({ fields, pagination, ordering, filters, summaryColumns }: GetHandsParams) =>
  async (): Promise<GetHandsResponse> => {
    const query = handsQuery(
      columnsToFields([...fields.value, ...additionalHandFields, handId]),
      pagination.value,
      summaryColumns.value,
      ordering && ordering.value,
      filters && filters.value,
    )
    const res: GetHandsResponse = await graphQLClient.request(query)
    res.hands = R.pipe(
      res.hands,
      R.groupBy(hand => hand.hand_id),
      R.mapValues(hands =>
        hands.reduce((a, b) => (Object.keys(b).length > Object.keys(a).length ? b : a)),
      ),
      R.values,
    )
    return res
  }

const statsFields = [
  'total_ev_loss',
  'avg_ev_loss',
  'avg_ev_loss_as_pot',
  'avg_ev_loss_per_mistake',
  'avg_frequency_difference',
  'avg_gto_score',
  'count_hands',
  'total_moves',
] as HandsStatsFieldName[]

const uploadStatsQuery = gql`
  query Hand_statistics {
    hand_statistics {
      avg_correct_moves
      avg_ev_loss
      count_hands
      total_right_moves
      avg_right_moves
      avg_gto_score
      total_mistake_moves
      total_moves
    }
  }
`

export const getUploadStats = async (): Promise<{ hand_statistics: HandStatistics }> =>
  graphQLClient.request(uploadStatsQuery)

const handsStatisticsQuery = (filterParams: FilterParams) => gql`
  query Hands {
    hand_statistics (
      ${filteredBy(filterParams)}
    ) {
      ${statsFields.join('\n')}
    }
    gto_score_trend (
      ${filteredBy(filterParams)}
    )
  }
`

export const getHandsStatistics =
  (filterParams: Ref<FilterParams>) =>
  async (): Promise<{ hand_statistics: HandStatistics; gto_score_trend: number[] }> =>
    graphQLClient.request(handsStatisticsQuery(filterParams.value))

export type TableOption = {
  id:
    | 'moves_by_street' //
    | 'preflop_actions_table'
    | 'positions_table'
    | 'aggressor_defender_table'
  title: string
  text: string
  gql_param:
    | 'situation_street' //
    | 'pot_type'
    | 'player_position'
    | 'player_aggression_preflop'
  icon: string
}
export type SimpleTable = TableOption['id']

type SimpleTableQueryParams = { table: Ref<TableOption>; filterParams: Ref<FilterParams> }

const simpleTableQuery = ({ table, filterParams }: SimpleTableQueryParams) => gql`
  query Hands {
    ${table.value.id} (
      ${filteredBy(filterParams.value)}
    ){
      ${table.value.gql_param}
      best_moves
      inaccuracies
      correct_moves
      mistake_moves
      blunders
    }
  }
`

export type SimpleTableResponse = SimpleTableDataResponses[SimpleTable]

export const getSimpleTable =
  (params: SimpleTableQueryParams) => async (): Promise<SimpleTableResponse> =>
    graphQLClient.request(simpleTableQuery(params))

export const scoreBreakdownFields = [
  'best_moves',
  'inaccuracies',
  'correct_moves',
  'blunders',
  'mistake_moves',
] as const

export type TscoreBreakdown = Record<(typeof scoreBreakdownFields)[number], number>

type ScoreBreakdownResponse = {
  score_breakdown: TscoreBreakdown
}

export const getScoreBreakdown =
  (filterParams: Ref<FilterParams>) => async (): Promise<ScoreBreakdownResponse> => {
    const query = gql`
    query Hands {
      score_breakdown (
        ${filteredBy(filterParams.value)}
      ) {
        ${scoreBreakdownFields.join('\n')}
      }
    }
  `

    return graphQLClient.request(query)
  }
